import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Phone, 
  MapPin, 
  Calendar,
  Edit,
  Trash2,
  Clock
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const getLocalDateString = () => {
  const now = new Date();
  return now.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getLocalTimeZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, '0');
  const mins = String(Math.abs(offset) % 60).padStart(2, '0');
  return `${tz} (GMT${sign}${hours}:${mins})`;
};

const PatientManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const patients = [
    {
      id: 'P001',
      name: 'John Doe',
      age: 45,
      gender: 'Male',
      phone: '+91 98765 43210',
      emergencyContact: '+91 98765 43211',
      address: '123 Main Street, Downtown',
      bloodGroup: 'O+',
      allergies: ['Penicillin', 'Peanuts'],
      medicalHistory: ['Hypertension', 'Diabetes'],
      currentMedications: ['Metformin', 'Lisinopril'],
      lastVisit: '2024-01-15',
      status: 'active',
      assignedAmbulance: 'AMB-001',
      assignedStaff: 'Dr. Sarah Johnson'
    },
    {
      id: 'P002',
      name: 'Jane Smith',
      age: 32,
      gender: 'Female',
      phone: '+91 98765 43212',
      emergencyContact: '+91 98765 43213',
      address: '456 Oak Avenue, Suburban',
      bloodGroup: 'A-',
      allergies: ['Latex'],
      medicalHistory: ['Asthma'],
      currentMedications: ['Albuterol'],
      lastVisit: '2024-01-20',
      status: 'active',
      assignedAmbulance: 'AMB-002',
      assignedStaff: 'Dr. Mike Wilson'
    },
    {
      id: 'P003',
      name: 'Robert Johnson',
      age: 68,
      gender: 'Male',
      phone: '+91 98765 43214',
      emergencyContact: '+91 98765 43215',
      address: '789 Pine Road, Uptown',
      bloodGroup: 'B+',
      allergies: ['Sulfa drugs'],
      medicalHistory: ['Heart Disease', 'Arthritis'],
      currentMedications: ['Aspirin', 'Ibuprofen'],
      lastVisit: '2024-01-18',
      status: 'inactive',
      assignedAmbulance: null,
      assignedStaff: null
    },
    {
      id: 'P004',
      name: 'Emily Davis',
      age: 28,
      gender: 'Female',
      phone: '+91 98765 43216',
      emergencyContact: '+91 98765 43217',
      address: '321 Elm Street, Downtown',
      bloodGroup: 'AB+',
      allergies: [],
      medicalHistory: ['Migraine'],
      currentMedications: ['Sumatriptan'],
      lastVisit: '2024-01-22',
      status: 'active',
      assignedAmbulance: 'AMB-004',
      assignedStaff: 'Dr. Emily Chen'
    }
  ];

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         patient.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         patient.phone.includes(searchTerm);
    const matchesFilter = filterStatus === 'all' || patient.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'inactive': return 'status-offline';
      default: return 'status-offline';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'inactive': return 'Inactive';
      default: return 'Unknown';
    }
  };

  const handleAddPatient = () => {
    toast.success('Patient added successfully!');
    setShowAddModal(false);
  };

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="p-8 bg-neutral-50 min-h-screen">
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-neutral-900 mb-2 tracking-tight">Patient Management</h1>
        <p className="text-neutral-500">
          Manage patient information, medical history, and emergency contacts
        </p>
        <div className="flex items-center space-x-4 mt-2 text-sm text-neutral-400">
          <Clock className="w-4 h-4" />
          <span>{currentTime.toLocaleTimeString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalDateString()}</span>
          <span className="text-neutral-400">|</span>
          <span>{getLocalTimeZone()}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-2xl shadow-card border border-neutral-100 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Search Patients
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-300 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, ID, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-12 h-12 text-base"
              />
            </div>
          </div>
          <div className="min-w-[220px] w-full lg:w-auto">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Filter by Status
            </label>
            <div className="relative">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field h-12 text-base pr-10 appearance-none bg-neutral-50 focus:bg-white cursor-pointer transition-all"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
              >
                <option value="all">All Patients</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-400">
                <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </span>
            </div>
          </div>
          <div>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn-primary flex items-center space-x-2 h-12 px-6 text-base"
            >
              <Plus className="w-5 h-5" />
              <span>Add Patient</span>
            </button>
          </div>
        </div>
      </div>

      {/* Patients Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {filteredPatients.map((patient, index) => (
          <motion.div
            key={patient.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card hover:shadow-lg transition-shadow duration-300"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-neutral-900">{patient.name}</h3>
                <p className="text-sm text-neutral-500">ID: {patient.id}</p>
              </div>
              <span className={getStatusColor(patient.status)}>
                {getStatusText(patient.status)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm font-medium text-neutral-500">Age & Gender</p>
                <p className="text-sm text-neutral-900">{patient.age} years, {patient.gender}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-500">Blood Group</p>
                <p className="text-sm text-neutral-900">{patient.bloodGroup}</p>
              </div>
            </div>
            <div className="space-y-3 mb-4">
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">{patient.phone}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">{patient.address}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-neutral-300" />
                <span className="text-sm text-neutral-900">Last Visit: {patient.lastVisit}</span>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-500 mb-2">Allergies</p>
              <div className="flex flex-wrap gap-1">
                {patient.allergies.length > 0 ? (
                  patient.allergies.map((allergy, idx) => (
                    <span key={idx} className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                      {allergy}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-neutral-400">None</span>
                )}
              </div>
            </div>
            {patient.assignedAmbulance && (
              <div className="mb-4 p-3 bg-primary-50 rounded-xl">
                <p className="text-sm font-medium text-primary-900">Currently Assigned</p>
                <p className="text-xs text-primary-700">Ambulance: {patient.assignedAmbulance}</p>
                <p className="text-xs text-primary-700">Staff: {patient.assignedStaff}</p>
              </div>
            )}
            <div className="flex items-center justify-between pt-4 border-t border-neutral-100">
              <button
                onClick={() => handleViewDetails(patient)}
                className="text-sm text-primary-600 hover:text-primary-800 font-semibold"
              >
                View Details
              </button>
              <div className="flex space-x-2">
                <button className="p-2 text-neutral-300 hover:text-primary-500 transition-colors">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-2 text-neutral-300 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Patient Details Modal */}
      {selectedPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4 p-6 border-b border-neutral-100">
              <h2 className="text-xl font-bold text-neutral-900">Patient Details</h2>
              <button
                onClick={() => setSelectedPatient(null)}
                className="text-neutral-300 hover:text-neutral-600"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6 p-6">
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Personal Information</h3>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {selectedPatient.name}</p>
                  <p><span className="font-medium">ID:</span> {selectedPatient.id}</p>
                  <p><span className="font-medium">Age:</span> {selectedPatient.age} years</p>
                  <p><span className="font-medium">Gender:</span> {selectedPatient.gender}</p>
                  <p><span className="font-medium">Blood Group:</span> {selectedPatient.bloodGroup}</p>
                  <p><span className="font-medium">Phone:</span> {selectedPatient.phone}</p>
                  <p><span className="font-medium">Emergency Contact:</span> {selectedPatient.emergencyContact}</p>
                  <p><span className="font-medium">Address:</span> {selectedPatient.address}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-3">Medical Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-neutral-700">Medical History:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedPatient.medicalHistory.map((condition, idx) => (
                        <span key={idx} className="text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full">
                          {condition}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Current Medications:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedPatient.currentMedications.map((med, idx) => (
                        <span key={idx} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                          {med}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-700">Allergies:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedPatient.allergies.length > 0 ? (
                        selectedPatient.allergies.map((allergy, idx) => (
                          <span key={idx} className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded-full">
                            {allergy}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-neutral-400">None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-neutral-100 p-6">
              <button
                onClick={() => setSelectedPatient(null)}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <h2 className="text-xl font-bold text-neutral-900 mb-4 p-6 border-b border-neutral-100">Add New Patient</h2>
            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Full Name
                </label>
                <input type="text" className="input-field" placeholder="John Doe" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Age
                  </label>
                  <input type="number" className="input-field" placeholder="25" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Gender
                  </label>
                  <select className="input-field">
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Phone Number
                </label>
                <input type="tel" className="input-field" placeholder="+91 98765 43210" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Emergency Contact
                </label>
                <input type="tel" className="input-field" placeholder="+91 98765 43211" />
              </div>
            </div>
            <div className="flex space-x-3 mt-6 p-6 border-t border-neutral-100">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-3 border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleAddPatient}
                className="flex-1 btn-primary"
              >
                Add Patient
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PatientManagement; 